import { auth, db } from '../utils/firebase.js';
import { useAuthStore } from '../stores/useAuthStore.js';
import { doc, setDoc, getDoc, getDocs, updateDoc, runTransaction, query, collection, where } from "firebase/firestore";
import { _getGroupIcon } from '../helpers/groupIcons.js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { serverTimestamp } from "firebase/firestore";

export const _deleteMyProfile = async () => {
    const taskQuery = query(
        collection(db, 'tasks'),
        where('requestedBy', '==', auth.currentUser.uid),
        where('status', '==', 'pending'),
        where('changeType', '==', 'deleteProfile')
    );

    const querySnapshot = await getDocs(taskQuery);
    if (!querySnapshot.empty) {
        // There are pending tasks for this user. We will not submit another one.
        return true;
    }

    // disable the user profile
    const functionsInstance = getFunctions();
    const disableUserCallable = httpsCallable(functionsInstance, 'disableUser');
    var response = await disableUserCallable();
    if (!response) {
        return false;
    }

    let taskData = {
        'changeType': 'deleteProfile',
        'status': 'pending',
        'requestedAt': serverTimestamp(),
        'requestedBy': auth.currentUser.uid
    }

    const tasksRef = collection(db, 'tasks');
    await setDoc(doc(tasksRef), taskData);

    return true;
}

export const _getUser = async () => {
    const userRef = doc(db, 'users', auth.currentUser.uid);
    const userSnapshot = await getDoc(userRef);
    return userSnapshot.exists() ? userSnapshot.data() : null;
}

export const _getUserGroups = async () => {
    const userRef = doc(db, 'users', auth.currentUser.uid);
    var userGroups = [];
    var userSnapshot = await getDoc(userRef);
    var docSnapshot = userSnapshot.data();
    if (docSnapshot && docSnapshot.groups) {
        userGroups = docSnapshot.groups;
    }

    for (var groupId in userGroups) {
        const groupRef = doc(db, 'groups', groupId);
        var groupSnapshot = await getDoc(groupRef);
        var groupDoc = groupSnapshot.data();
        const icon = await _getGroupIcon(groupDoc.icon);
        userGroups[groupId] = { displayName: userGroups[groupId], icon: icon };
    }

    return userGroups;
}

export const _getUserRatedGames = async (groupId) => {
    const userRef = doc(db, 'groups', groupId, 'ratings', auth.currentUser.uid);
    var userSnapshot = await getDoc(userRef);
    var docSnapshot = userSnapshot.data();
    if (!docSnapshot) {
        return {};
    }

    var response = docSnapshot || {};
    return response;
}

export const _isUserInGroup = async (groupId) => {
    const userRef = doc(db, 'users', auth.currentUser.uid);
    var userSnapshot = await getDoc(userRef);
    var docSnapshot = userSnapshot.data();
    if (!docSnapshot || !docSnapshot.groups) {
        return false;
    }
    return docSnapshot.groups[groupId] || false;
}

export const _leaveGroup = async (groupId) => {
    const functionsInstance = getFunctions();
    const removeMemberFromGroupCallable = httpsCallable(functionsInstance, 'leaveGroup');
    const result = await removeMemberFromGroupCallable({ groupId: groupId });
    return result;
}

export const postSignInCheck = async (displayName) => {
    const userRef = doc(db, 'users', auth.currentUser.uid);
    var userSnapshot = await getDoc(userRef);
    var docSnapshot = userSnapshot.data();
    var userData = { lastLogin: new Date() };
    if (!docSnapshot) {
        userData.dateJoined = new Date();
        userData.displayName = displayName || auth.currentUser.displayName;
        await setDoc(userRef, userData);
    } else {
        if (!docSnapshot.displayName) {
            userData.displayName = displayName || auth.currentUser.displayName;
        } else {
            displayName = docSnapshot.displayName;
        }
        await updateDoc(userRef, userData);
    }

    useAuthStore.getState().setDisplayName(displayName || auth.currentUser.displayName || "Name not set");
}

export const _requestMyData = async () => {
    const taskQuery = query(
        collection(db, 'tasks'),
        where('requestedBy', '==', auth.currentUser.uid),
        where('status', '==', 'pending'),
        where('changeType', '==', 'requestMyData')
    );
    const querySnapshot = await getDocs(taskQuery);
    if (!querySnapshot.empty) {
        // There are pending tasks for this user. We will not submit another one.
        return true;
    }

    let taskData = {
        'changeType': 'requestMyData',
        'status': 'pending',
        'requestedAt': serverTimestamp(),
        'requestedBy': auth.currentUser.uid
    }

    const tasksRef = collection(db, 'tasks');
    await setDoc(doc(tasksRef), taskData);
}

export const _saveUserDisplayName = async (newDisplayName, changeInGroups) => {
    const getUserGroups = async () => {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        const userSnapshot = await getDoc(userRef);
        const docSnapshot = userSnapshot.data();
        return docSnapshot.groups || [];
    }

    var userData = { displayName: newDisplayName };

    const userGroups = await getUserGroups();
    const userGroupsToUpdate = {};

    if (!changeInGroups) {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        await updateDoc(userRef, userData, { merge: true });
        return;
    }

    // loop through the groups and get the members' display name in each group
    for (const groupId of Object.keys(userGroups)) {
        const groupQuery = query(collection(db, 'groups', groupId, 'members'), where('uid', '==', auth.currentUser.uid));
        const querySnapshot = await getDocs(groupQuery);
        querySnapshot.forEach((doc) => {
            userGroupsToUpdate[groupId] = { "id": doc.id, displayName: userData.displayName };
        });
    }

    await runTransaction(db, async (transaction) => {
        // update the member documents
        const userRef = doc(db, 'users', auth.currentUser.uid);
        await transaction.update(userRef, userData, { merge: true });

        // update the group members
        Object.keys(userGroupsToUpdate).forEach(async (groupId) => {
            const groupMemberRef = doc(db, 'groups', groupId, 'members', userGroupsToUpdate[groupId].id);
            await transaction.update(groupMemberRef, { displayName: userData.displayName }, { merge: true });
        });
    });
}